import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { DASH } from '@/constants/testIds';
import { Plus, Trash2, FileText, Calendar, CreditCard, Sparkles, ArrowUpDown, Copy, Settings as SettingsIcon, Folder, FolderPlus, FolderOpen, FolderMinus, Pencil, X, Check, ChevronDown, Search, Briefcase } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { TRADES } from '@/lib/calculators';
import Seo from '@/components/Seo';
import HomeScreenHint from '@/components/HomeScreenHint';
import { TrackingLine, RecentActivity } from '@/components/QuoteTracking';
import { TRACK_FILTERS, matchesTrack } from '@/lib/tracking';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'amount-desc', label: 'Highest £ first' },
  { value: 'amount-asc', label: 'Lowest £ first' },
  { value: 'number-asc', label: 'Quote no. (low → high)' },
  { value: 'number-desc', label: 'Quote no. (high → low)' },
  { value: 'name-asc', label: 'Name (A → Z)' },
];

const STATUSES = {
  draft:    { label: 'Draft',    cls: 'bg-slate-200 text-slate-900' },
  sent:     { label: 'Sent',     cls: 'bg-blue-200 text-blue-900' },
  accepted: { label: 'Accepted', cls: 'bg-emerald-200 text-emerald-900' },
  declined: { label: 'Declined', cls: 'bg-rose-200 text-rose-900' },
  paid:     { label: 'Paid',     cls: 'bg-brand text-white' },
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isPro = user?.is_pro || user?.role === 'admin';
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('newest');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [trackFilter, setTrackFilter] = useState('all');
  const [folders, setFolders] = useState([]);
  const [folderFilter, setFolderFilter] = useState('all'); // 'all' | 'unfiled' | <folder_id>
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [folderMenuOpen, setFolderMenuOpen] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const folderMenuRef = useRef(null);

  // Close folder dropdown when clicking outside
  useEffect(() => {
    if (!folderMenuOpen) return;
    const onClick = (e) => {
      if (folderMenuRef.current && !folderMenuRef.current.contains(e.target)) {
        setFolderMenuOpen(false);
        setFolderSearch('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [folderMenuOpen]);

  const load = async () => {
    setLoading(true);
    try {
      const [projRes, foldersRes] = await Promise.all([
        api.get('/projects'),
        api.get('/folders').catch(() => ({ data: [] })),
      ]);
      setProjects(projRes.data);
      setFolders(foldersRes.data || []);
    } catch { toast.error('Could not load quotes'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Quietly refresh view tracking every minute so new customer views show up
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') api.get('/projects').then(({ data }) => setProjects(data)).catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, []);

  // Folder helpers — keep counts in sync without a full reload
  const refreshFolders = async () => {
    try { const { data } = await api.get('/folders'); setFolders(data || []); } catch { /* ignore */ }
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const { data } = await api.post('/folders', { name });
      setFolders((f) => [...f, data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewFolderName('');
      setCreatingFolder(false);
      toast.success(`Folder "${data.name}" created`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create folder');
    }
  };

  const renameFolder = async (id) => {
    const name = editingFolderName.trim();
    if (!name) { setEditingFolderId(null); return; }
    try {
      const { data } = await api.patch(`/folders/${id}`, { name });
      setFolders((f) => f.map((x) => x.id === id ? data : x).sort((a, b) => a.name.localeCompare(b.name)));
      setEditingFolderId(null);
      setEditingFolderName('');
      toast.success('Folder renamed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not rename folder');
    }
  };

  const deleteFolder = async (id, name) => {
    if (!window.confirm(`Delete folder "${name}"? Quotes inside will be kept and moved back to Unfiled.`)) return;
    try {
      await api.delete(`/folders/${id}`);
      setFolders((f) => f.filter((x) => x.id !== id));
      // Locally un-set folder_id on any project that was inside it
      setProjects((p) => p.map((x) => x.folder_id === id ? { ...x, folder_id: null } : x));
      if (folderFilter === id) setFolderFilter('all');
      toast.success('Folder deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete folder');
    }
  };

  const moveProjectToFolder = async (projectId, folderId) => {
    try {
      const { data } = await api.patch(`/projects/${projectId}/folder`, { folder_id: folderId || null });
      setProjects((p) => p.map((x) => x.id === projectId ? data : x));
      await refreshFolders();
      const folderName = folderId ? folders.find((f) => f.id === folderId)?.name : null;
      toast.success(folderName ? `Moved to "${folderName}"` : 'Removed from folder');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not move quote');
    }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects((p) => p.filter((x) => x.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  const openPortal = async () => {
    try {
      const { data } = await api.post('/billing/portal', { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open billing portal');
    }
  };

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/projects/${id}/duplicate`);
      setProjects((p) => [data, ...p]);
      toast.success(`Duplicated as ${data.quote_number}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Duplicate failed');
    }
  };

  const createJob = async (p) => {
    if (!isPro) { toast.error('The Job Tracker is a Pro feature. Start your 14-day free trial.'); return; }
    try {
      const { data } = await api.post('/jobs', { name: p.name || 'New job', quote_id: p.id });
      toast.success('Job created — tracking profit now');
      navigate(`/jobs/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create job'); }
  };

  const createInvoice = async (p) => {
    if (!isPro) { toast.error('Invoices are a Pro feature. Start your 14-day free trial.'); return; }
    try {
      const { data } = await api.post(`/invoices/from-project/${p.id}`);
      toast.success('Invoice created from quote');
      navigate(`/invoices/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not create invoice'); }
  };

  const setStatus = async (id, newStatus) => {
    try {
      const { data } = await api.patch(`/projects/${id}/status`, { status: newStatus });
      setProjects((p) => p.map((x) => x.id === id ? data : x));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status update failed');
    }
  };

  const visibleProjects = useMemo(() => {
    let arr = [...projects];
    if (folderFilter === 'unfiled') arr = arr.filter((p) => !p.folder_id);
    else if (folderFilter !== 'all') arr = arr.filter((p) => p.folder_id === folderFilter);
    if (statusFilter !== 'all') arr = arr.filter((p) => (p.status || 'draft') === statusFilter);
    if (trackFilter !== 'all') arr = arr.filter((p) => matchesTrack(p, trackFilter));
    if (search.trim()) {
      const q = search.toLowerCase();
      const folderNameById = Object.fromEntries(folders.map((f) => [f.id, f.name.toLowerCase()]));
      arr = arr.filter((p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.quote_number || '').toLowerCase().includes(q) ||
        (p.customer_name || '').toLowerCase().includes(q) ||
        (p.trade || '').toLowerCase().includes(q) ||
        (folderNameById[p.folder_id] || '').includes(q)
      );
    }
    const cmp = {
      'newest':       (a, b) => (b.created_at || '').localeCompare(a.created_at || ''),
      'oldest':       (a, b) => (a.created_at || '').localeCompare(b.created_at || ''),
      'amount-desc':  (a, b) => (b.total || 0) - (a.total || 0),
      'amount-asc':   (a, b) => (a.total || 0) - (b.total || 0),
      'number-asc':   (a, b) => (a.quote_number || '').localeCompare(b.quote_number || '', undefined, { numeric: true }),
      'number-desc':  (a, b) => (b.quote_number || '').localeCompare(a.quote_number || '', undefined, { numeric: true }),
      'name-asc':     (a, b) => (a.name || '').localeCompare(b.name || ''),
    }[sort];
    arr.sort(cmp);
    return arr;
  }, [projects, sort, search, statusFilter, trackFilter, folderFilter, folders]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <Seo title="Quotes" noindex />
      <Toaster richColors position="top-right" />
      <HomeScreenHint />
      <header className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-brand font-semibold">Sales &amp; customers</p>
          <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">
            Quotes
          </h1>
          <p className="text-slate-600 mt-2">Your saved quotes and projects.</p>
        </div>
        <Link to="/estimator" data-testid={DASH.newProject} className="tm-btn">
          <Plus size={16}/> New Quote
        </Link>
      </header>

      <div className="mb-4">
        <Link to="/settings" data-testid="dash-settings-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-600 hover:text-brand font-semibold">
          <SettingsIcon size={14}/> My account
        </Link>
      </div>

      {user?.is_pro ? (
        <div className="tm-card p-4 mb-6 flex items-center justify-between gap-3 bg-slate-50">
          <div className="flex items-center gap-3">
            <span className="tm-chip tm-chip-orange">PRO</span>
            <p className="text-xs uppercase tracking-widest text-slate-700 font-semibold">
              {user.subscription_status === 'trialing' ? 'Free trial active' : 'Subscription active'}
            </p>
          </div>
          <button data-testid="dash-billing-portal" onClick={openPortal} className="tm-btn tm-btn-ghost text-xs py-2 min-h-0">
            <CreditCard size={14}/> Manage billing
          </button>
        </div>
      ) : (
        <div className="tm-card tm-card-dark p-4 mb-6 border border-slate-200 shadow-md flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Sparkles className="text-brand" size={20}/>
            <p className="text-xs uppercase tracking-widest font-semibold">Upgrade to Pro (£5/mo) to save quotes & branded invoices · or Pro+ (£9.99) for AI estimator + customer share links</p>
          </div>
          <Link to="/pricing" data-testid="dash-upgrade-cta" className="tm-btn text-xs py-2 min-h-0">See plans</Link>
        </div>
      )}

      {loading ? (
        <p className="uppercase tracking-widest text-slate-500 font-semibold">Loading…</p>
      ) : projects.length === 0 ? (
        <div className="tm-card p-10 text-center">
          <FileText className="mx-auto text-brand" size={48}/>
          <h3 className="font-display font-bold text-2xl uppercase tracking-tight mt-4">No quotes yet</h3>
          <p className="text-slate-600 mt-2">Build your first estimate — labour, materials, VAT and margin in one place.</p>
          <Link to="/estimator" className="tm-btn mt-6 inline-flex">Start a quote</Link>
        </div>
      ) : (
        <>
          <div className="tm-card p-4 mb-6 flex flex-wrap items-center gap-3 justify-between">
            <input
              data-testid="dash-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, customer, quote number or trade…"
              className="tm-input flex-1 min-w-[200px]"
            />
            <div className="flex items-center gap-2">
              <ArrowUpDown size={16} className="text-slate-500"/>
              <select
                data-testid="dash-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="tm-input bg-white pr-8"
              >
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div className="mb-5">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Folders</p>
              {!creatingFolder ? (
                <button
                  data-testid="folder-new-btn"
                  onClick={() => setCreatingFolder(true)}
                  className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-slate-600 hover:text-brand font-semibold"
                >
                  <FolderPlus size={14}/> New folder
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <input
                    data-testid="folder-new-input"
                    autoFocus
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); } }}
                    placeholder="Folder name…"
                    className="border border-slate-200 px-2 py-1 text-xs font-mono"
                  />
                  <button data-testid="folder-new-save" onClick={createFolder} className="bg-brand text-white border border-slate-200 p-1.5" aria-label="Save"><Check size={14}/></button>
                  <button onClick={() => { setCreatingFolder(false); setNewFolderName(''); }} className="border border-slate-200 bg-white p-1.5" aria-label="Cancel"><X size={14}/></button>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Quick chips: All / Unfiled */}
              {[
                { id: 'all',     name: 'All quotes', count: projects.length },
                { id: 'unfiled', name: 'Unfiled',    count: projects.filter((p) => !p.folder_id).length },
              ].map((f) => {
                const active = folderFilter === f.id;
                return (
                  <button
                    key={f.id}
                    data-testid={`folder-chip-${f.id}`}
                    onClick={() => setFolderFilter(f.id)}
                    className={`rounded-full inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 font-display font-bold uppercase text-xs tracking-wider transition-colors ${
                      active ? 'bg-slate-900 text-white' : 'bg-white text-slate-900 hover:bg-slate-100'
                    }`}
                  >
                    {f.id === 'all' ? <FolderOpen size={14}/> : <FolderMinus size={14}/>}
                    {f.name} <span className="font-mono ml-1 opacity-75">({f.count})</span>
                  </button>
                );
              })}

              {/* Folder dropdown — keeps the bar tidy even with hundreds of folders */}
              {folders.length > 0 && (
                <div className="relative" ref={folderMenuRef}>
                  {(() => {
                    const activeFolder = folders.find((f) => f.id === folderFilter);
                    return (
                      <button
                        data-testid="folder-dropdown-trigger"
                        onClick={() => setFolderMenuOpen((v) => !v)}
                        className={`rounded-full inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 font-display font-bold uppercase text-xs tracking-wider transition-colors ${
                          activeFolder ? 'bg-slate-900 text-white' : 'bg-white text-slate-900 hover:bg-slate-100'
                        }`}
                      >
                        <Folder size={14}/>
                        {activeFolder ? (
                          <>
                            <span className="max-w-[160px] truncate">{activeFolder.name}</span>
                            <span className="font-mono opacity-75">({activeFolder.count})</span>
                          </>
                        ) : (
                          <>Folders <span className="font-mono opacity-75">({folders.length})</span></>
                        )}
                        <ChevronDown size={14}/>
                      </button>
                    );
                  })()}
                  {folderMenuOpen && (
                    <div
                      data-testid="folder-dropdown-panel"
                      className="absolute z-50 top-full left-0 mt-1 w-[320px] max-w-[90vw] bg-white border border-slate-200 shadow-md"
                    >
                      <div className="p-2 border-b border-slate-200 flex items-center gap-2">
                        <Search size={14} className="text-slate-500 shrink-0"/>
                        <input
                          data-testid="folder-dropdown-search"
                          autoFocus
                          value={folderSearch}
                          onChange={(e) => setFolderSearch(e.target.value)}
                          placeholder="Search folders…"
                          className="flex-1 outline-none font-mono text-xs py-1 placeholder:text-slate-400"
                        />
                        {folderSearch && (
                          <button onClick={() => setFolderSearch('')} className="text-slate-400 hover:text-slate-700" aria-label="Clear">
                            <X size={14}/>
                          </button>
                        )}
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {folders
                          .filter((f) => f.name.toLowerCase().includes(folderSearch.toLowerCase()))
                          .map((f) => {
                            const active = folderFilter === f.id;
                            const editing = editingFolderId === f.id;
                            if (editing) {
                              return (
                                <div key={f.id} className="flex items-center gap-1 p-2 border-b border-slate-100">
                                  <input
                                    autoFocus
                                    value={editingFolderName}
                                    onChange={(e) => setEditingFolderName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') renameFolder(f.id); if (e.key === 'Escape') setEditingFolderId(null); }}
                                    className="flex-1 border border-brand px-2 py-1 text-xs font-mono"
                                  />
                                  <button data-testid={`folder-rename-save-${f.id}`} onClick={() => renameFolder(f.id)} className="bg-brand text-white p-1.5" aria-label="Save"><Check size={12}/></button>
                                  <button onClick={() => setEditingFolderId(null)} className="border border-slate-300 bg-white p-1.5" aria-label="Cancel"><X size={12}/></button>
                                </div>
                              );
                            }
                            return (
                              <div
                                key={f.id}
                                className={`flex items-center border-b border-slate-100 last:border-b-0 group ${active ? 'bg-slate-900 text-white' : 'hover:bg-slate-50'}`}
                              >
                                <button
                                  data-testid={`folder-chip-${f.id}`}
                                  onClick={() => { setFolderFilter(f.id); setFolderMenuOpen(false); setFolderSearch(''); }}
                                  className={`flex-1 flex items-center gap-2 px-3 py-2 text-left font-display font-bold uppercase text-xs tracking-wider`}
                                >
                                  <Folder size={14}/>
                                  <span className="flex-1 truncate">{f.name}</span>
                                  <span className={`font-mono opacity-75 ${active ? 'text-white' : 'text-slate-500'}`}>({f.count})</span>
                                </button>
                                <button
                                  data-testid={`folder-edit-${f.id}`}
                                  onClick={() => { setEditingFolderId(f.id); setEditingFolderName(f.name); }}
                                  className={`px-2 py-2 opacity-60 hover:opacity-100 ${active ? 'text-white hover:text-brand' : 'text-slate-500 hover:text-brand'}`}
                                  title="Rename folder"
                                >
                                  <Pencil size={12}/>
                                </button>
                                <button
                                  data-testid={`folder-delete-${f.id}`}
                                  onClick={() => deleteFolder(f.id, f.name)}
                                  className={`px-2 py-2 opacity-60 hover:opacity-100 ${active ? 'text-white hover:text-red-300' : 'text-slate-500 hover:text-red-600'}`}
                                  title="Delete folder (quotes inside are kept)"
                                >
                                  <Trash2 size={12}/>
                                </button>
                              </div>
                            );
                          })}
                        {folders.filter((f) => f.name.toLowerCase().includes(folderSearch.toLowerCase())).length === 0 && (
                          <p className="font-mono text-xs text-slate-400 p-4 text-center">No folders match &quot;{folderSearch}&quot;</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <RecentActivity projects={projects} />

          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="dash-track-filters">
            <span className="tm-label mb-0 mr-1">Customer</span>
            {TRACK_FILTERS.map((f) => {
              const count = projects.filter((p) => matchesTrack(p, f.id)).length;
              const active = trackFilter === f.id;
              return (
                <button
                  key={f.id}
                  data-testid={`dash-track-${f.id}`}
                  onClick={() => setTrackFilter(f.id)}
                  className={`rounded-full px-3 py-1.5 border text-xs font-semibold transition-colors ${
                    active ? 'bg-brand text-brand-ink border-brand' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {f.label} <span className="ml-1 opacity-75">({count})</span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-5">
            <span className="tm-label mb-0 mr-1">Status</span>
            {['all', 'draft', 'sent', 'accepted', 'declined', 'paid'].map((s) => {
              const count = s === 'all'
                ? projects.length
                : projects.filter((p) => (p.status || 'draft') === s).length;
              const active = statusFilter === s;
              const label = s === 'all' ? 'All' : STATUSES[s].label;
              return (
                <button
                  key={s}
                  data-testid={`dash-filter-${s}`}
                  onClick={() => setStatusFilter(s)}
                  className={`rounded-full px-3 py-1.5 border border-slate-200 font-display font-bold uppercase text-xs tracking-wider transition-colors ${
                    active ? 'bg-slate-900 text-white' : 'bg-white text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  {label} <span className="font-mono ml-1">({count})</span>
                </button>
              );
            })}
          </div>

          {visibleProjects.length === 0 ? (
            <p className="font-mono text-sm text-slate-500">No quotes match your filters.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {visibleProjects.map((p) => {
                const tradeName = TRADES.find((t) => t.slug === p.trade)?.name || p.trade;
                const date = p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB') : '';
                const st = STATUSES[p.status || 'draft'];
                return (
                  <article key={p.id} data-testid={DASH.projectCard(p.id)} className="tm-card p-6 flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        {p.quote_number && (
                          <span className="tm-chip tm-chip-orange font-mono">{p.quote_number}</span>
                        )}
                        <span className={`tm-chip ${st.cls}`} data-testid={`dash-status-${p.id}`}>{st.label}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`dash-duplicate-${p.id}`}
                          onClick={() => duplicate(p.id)}
                          title="Duplicate quote"
                          className="text-slate-500 hover:text-brand p-1"
                        >
                          <Copy size={16}/>
                        </button>
                        <button data-testid={DASH.delete(p.id)} onClick={() => del(p.id)} className="text-slate-500 hover:text-red-600 p-1" title="Delete">
                          <Trash2 size={16}/>
                        </button>
                      </div>
                    </div>
                    <h3 className="font-display font-bold text-xl uppercase tracking-tight">{p.name}</h3>
                    {p.customer_name && (
                      <p className="text-xs text-slate-600 uppercase tracking-wider font-semibold">For: {p.customer_name}</p>
                    )}
                    {p.folder_id && (
                      <p className="text-[10px] text-slate-500 uppercase tracking-widest inline-flex items-center gap-1 font-semibold">
                        <Folder size={11}/> {folders.find((f) => f.id === p.folder_id)?.name || 'Folder'}
                      </p>
                    )}
                    <p className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1 font-semibold"><Calendar size={12}/> {tradeName} · {date}</p>
                    <TrackingLine p={p} />
                    <div className="tm-result mt-2">
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs uppercase tracking-widest text-slate-300">Total</span>
                        <span className="font-mono font-semibold text-2xl text-brand">£{p.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/estimator/${p.id}`} className="tm-btn tm-btn-dark text-xs min-h-0 py-2">
                        Open quote →
                      </Link>
                      {isPro && (
                        <button
                          data-testid={`dash-create-job-${p.id}`}
                          onClick={() => createJob(p)}
                          title="Create a Job in the Job Tracker, pre-filled from this quote"
                          className="tm-btn text-xs min-h-0 py-2"
                        >
                          <Briefcase size={14}/> → Job
                        </button>
                      )}
                      {isPro && (
                        <button
                          data-testid={`dash-create-invoice-${p.id}`}
                          onClick={() => createInvoice(p)}
                          title="Turn this quote into an invoice you can send to the customer"
                          className="tm-btn text-xs min-h-0 py-2"
                        >
                          <FileText size={14}/> → Invoice
                        </button>
                      )}
                      <select
                        data-testid={`dash-set-status-${p.id}`}
                        value={p.status || 'draft'}
                        onChange={(e) => setStatus(p.id, e.target.value)}
                        className="border border-slate-200 bg-white text-xs uppercase tracking-wider py-2 px-2 min-h-0 font-semibold"
                      >
                        {Object.keys(STATUSES).map((s) => (
                          <option key={s} value={s}>{`Mark: ${STATUSES[s].label}`}</option>
                        ))}
                      </select>
                      <select
                        data-testid={`dash-move-folder-${p.id}`}
                        value={p.folder_id || ''}
                        onChange={(e) => moveProjectToFolder(p.id, e.target.value || null)}
                        className="border border-slate-200 bg-white text-xs uppercase tracking-wider py-2 px-2 min-h-0 font-semibold"
                        title="Move quote to a folder"
                      >
                        <option value="">No folder</option>
                        {folders.map((f) => (
                          <option key={f.id} value={f.id}>{`📁 ${f.name}`}</option>
                        ))}
                      </select>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
